import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Eyebrow,
  Field,
  Input,
  Select,
  Spinner,
  ApiError,
} from '@busbook/ui';
import {
  MENU_STYLES,
  type CustomerFeature,
  type MenuItem,
  type MenuStyle,
} from '@busbook/shared';
import { api } from '../lib/api.js';

/**
 * What a tenant's passengers get: navigation shape, which pages exist, which capabilities
 * are switched on (DECISIONS D69).
 *
 * This is the platform's decision, not the operator's, which is the whole point of it living
 * here. An operator picks their name, colours and logo in their own console; what the product
 * IS — how many pages, whether passengers can email a ticket or save travellers — is what
 * distinguishes one plan from the next, and is therefore sold rather than self-served.
 *
 * The same control shape for both lists on purpose: an ordered list with a rename and a
 * toggle. They are the same job — deciding what a passenger sees, and in what order.
 */

interface Experience {
  menuStyle: MenuStyle;
  menuItems: MenuItem[];
  customerFeatures: CustomerFeature[];
}

/**
 * Named for what a passenger sees, with a note saying when to pick it. Five shapes is past
 * the point where a name alone tells you which one this operator should be sold — "rail"
 * means nothing until you know it only becomes a rail on a wide screen.
 */
const MENU_STYLE_LABEL: Record<MenuStyle, string> = {
  BOTTOM_TABS: 'Bottom tabs',
  FLOATING_DOCK: 'Floating dock',
  SIDE_DRAWER: 'Side drawer',
  TOP_BAR: 'Top bar',
  RAIL: 'Side rail',
};

const MENU_STYLE_NOTE: Record<MenuStyle, string> = {
  BOTTOM_TABS: 'Grounded bar across the bottom, under the thumb. The safe choice for up to five pages.',
  FLOATING_DOCK: 'A detached bar the page scrolls beneath. Best with three or four pages and room to breathe.',
  SIDE_DRAWER: 'Behind a menu button in the header. The only shape that stays usable past five pages.',
  TOP_BAR: 'Always visible above the content; the pages scroll sideways on a phone.',
  RAIL: 'An icon rail down the side on a laptop, a floating dock on a phone. For operators whose passengers book at a desk.',
};

export function ExperienceEditor({ tenantId }: { tenantId: string }) {
  const [draft, setDraft] = useState<Experience | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const result = await api.get<Experience>(`/platform/tenants/${tenantId}/experience`);
        if (!cancelled) setDraft(result);
      } catch {
        if (!cancelled) setMessage({ tone: 'error', text: 'Could not load this tenant’s app.' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  function patch(next: Partial<Experience>) {
    setDraft((current) => (current ? { ...current, ...next } : current));
    // A success notice that outlives the value it described is a lie about unsaved work.
    setMessage(null);
  }

  /** Reorder in place. Both lists are short and ordered, so arrows beat drag-and-drop —
   *  and unlike drag, they work from a keyboard without a second implementation. */
  function move<T>(list: T[], index: number, direction: -1 | 1): T[] {
    const target = index + direction;
    if (target < 0 || target >= list.length) return list;
    const next = [...list];
    [next[index], next[target]] = [next[target]!, next[index]!];
    return next;
  }

  async function save() {
    if (!draft) return;
    setBusy(true);
    setMessage(null);
    try {
      const saved = await api.put<Experience>(`/platform/tenants/${tenantId}/experience`, draft);
      setDraft(saved);
      setMessage({ tone: 'success', text: 'Saved. Passengers see it on their next load.' });
    } catch (error) {
      setMessage({
        tone: 'error',
        text:
          error instanceof ApiError ? error.message : 'Could not save. Try again.',
      });
    } finally {
      setBusy(false);
    }
  }

  if (!draft) {
    return (
      <Card>
        <Eyebrow>Passenger app</Eyebrow>
        <div className="grid place-items-center py-10">
          {message ? <Alert tone="error">{message.text}</Alert> : <Spinner label="Loading" />}
        </div>
      </Card>
    );
  }

  const visiblePages = draft.menuItems.filter((item) => item.visible).length;

  return (
    <Card>
      <Eyebrow>Passenger app</Eyebrow>
      <p className="mt-1 text-sm" style={{ color: 'var(--color-muted)' }}>
        Which pages and features this operator’s passengers get. The operator sets their own
        name, colours and logo; this is what they were sold.
      </p>

      <div className="mt-4">
        <Field label="Navigation" htmlFor="menuStyle">
          <Select
            id="menuStyle"
            value={draft.menuStyle}
            onChange={(e) => patch({ menuStyle: e.target.value as MenuStyle })}
          >
            {MENU_STYLES.map((style) => (
              <option key={style} value={style}>
                {MENU_STYLE_LABEL[style]}
              </option>
            ))}
          </Select>
        </Field>
        <p className="mt-1.5 text-xs" style={{ color: 'var(--color-muted)' }}>
          {MENU_STYLE_NOTE[draft.menuStyle]}
        </p>
      </div>

      <div className="mt-6">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="text-sm font-semibold">Pages</h3>
          <span className="text-xs" style={{ color: 'var(--color-muted)' }}>
            {/* Bottom tabs stop being usable past about five, so the count is worth seeing
                while deciding rather than after the operator complains. */}
            {visiblePages} shown
          </span>
        </div>

        <ul className="mt-2 flex flex-col gap-2">
          {draft.menuItems.map((item, index) => (
            <Row
              key={item.key}
              name={item.key}
              label={item.label}
              checked={item.visible}
              first={index === 0}
              last={index === draft.menuItems.length - 1}
              onMove={(direction) => patch({ menuItems: move(draft.menuItems, index, direction) })}
              onLabel={(label) =>
                patch({
                  menuItems: draft.menuItems.map((entry, i) =>
                    i === index ? { ...entry, label } : entry,
                  ),
                })
              }
              onToggle={(visible) =>
                patch({
                  menuItems: draft.menuItems.map((entry, i) =>
                    i === index ? { ...entry, visible } : entry,
                  ),
                })
              }
            />
          ))}
        </ul>
      </div>

      <div className="mt-6">
        <h3 className="text-sm font-semibold">Features</h3>
        <p className="mt-1 text-xs" style={{ color: 'var(--color-muted)' }}>
          Order decides the sequence these appear in as actions on a trip.
        </p>

        <ul className="mt-2 flex flex-col gap-2">
          {draft.customerFeatures.map((feature, index) => (
            <Row
              key={feature.key}
              name={feature.key}
              label={feature.label}
              checked={feature.visible}
              first={index === 0}
              last={index === draft.customerFeatures.length - 1}
              onMove={(direction) =>
                patch({ customerFeatures: move(draft.customerFeatures, index, direction) })
              }
              onLabel={(label) =>
                patch({
                  customerFeatures: draft.customerFeatures.map((entry, i) =>
                    i === index ? { ...entry, label } : entry,
                  ),
                })
              }
              onToggle={(visible) =>
                patch({
                  customerFeatures: draft.customerFeatures.map((entry, i) =>
                    i === index ? { ...entry, visible } : entry,
                  ),
                })
              }
            />
          ))}
        </ul>
      </div>

      {message && (
        <div className="mt-4">
          <Alert tone={message.tone}>{message.text}</Alert>
        </div>
      )}

      <div className="mt-5">
        <Button loading={busy} onClick={save}>
          Save passenger app
        </Button>
      </div>
    </Card>
  );
}

function Row({
  name,
  label,
  checked,
  first,
  last,
  onMove,
  onLabel,
  onToggle,
}: {
  name: string;
  label: string;
  checked: boolean;
  first: boolean;
  last: boolean;
  onMove: (direction: -1 | 1) => void;
  onLabel: (label: string) => void;
  onToggle: (visible: boolean) => void;
}) {
  return (
    <li
      className="flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2"
      style={{ borderColor: 'var(--color-line)' }}
    >
      <span className="flex shrink-0 gap-1">
        <Button
          size="sm"
          variant="ghost"
          disabled={first}
          aria-label={`Move ${name} up`}
          onClick={() => onMove(-1)}
        >
          ↑
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={last}
          aria-label={`Move ${name} down`}
          onClick={() => onMove(1)}
        >
          ↓
        </Button>
      </span>

      <span className="w-24 shrink-0 text-xs" style={{ color: 'var(--color-muted)' }}>
        {name}
      </span>

      <Input
        className="min-w-0 flex-1"
        value={label}
        aria-label={`Label for ${name}`}
        onChange={(e) => onLabel(e.target.value)}
      />

      <label className="flex shrink-0 items-center gap-2 text-sm">
        <input type="checkbox" checked={checked} onChange={(e) => onToggle(e.target.checked)} />
        Show
      </label>
    </li>
  );
}
